import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Divider,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tab,
  Tabs,
} from '@mui/material';
import {
  CalendarToday,
  LocationOn,
  DirectionsCar,
  Cancel as CancelIcon,
  CheckCircle,
  Pending,
  HourglassEmpty,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { reservationApi } from '../services/api';
import { Reservation, ReservationStatus } from '../types/reservation';
import { ReservationStatusChip } from '../components/ReservationStatusChip';
import { formatDate, formatCurrency } from '../utils/formatters';
import { useTranslation } from 'react-i18next';
import { APP_ROUTES } from '../routing/appRoutes';

export const ReservationsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedReservation, setSelectedReservation] = useState<Reservation | null>(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  // Fetch user's reservations
  const { data: reservations, isLoading, error } = useQuery(
    'userReservations',
    reservationApi.getUserReservations
  );

  // Cancel reservation mutation
  const cancelMutation = useMutation(
    (id: string) => reservationApi.cancel(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('userReservations');
        setCancelDialogOpen(false);
        setSelectedReservation(null);
        setCancelReason('');
      },
    }
  );

  const handleCancelClick = (reservation: Reservation) => {
    setSelectedReservation(reservation);
    setCancelDialogOpen(true);
  };

  const handleConfirmCancel = () => {
    if (selectedReservation) {
      cancelMutation.mutate(selectedReservation.id.toString());
    }
  };

  const getStatusIcon = (status: ReservationStatus) => {
    switch (status) {
      case ReservationStatus.CONFIRMED:
        return <CheckCircle />;
      case ReservationStatus.PENDING:
        return <Pending />;
      case ReservationStatus.DECLINED:
        return <CancelIcon />;
      case ReservationStatus.IN_PROGRESS:
        return <HourglassEmpty />;
      case ReservationStatus.COMPLETED:
        return <CheckCircle />;
      case ReservationStatus.CANCELLED:
        return <CancelIcon />;
      default:
        return <Pending />;
    }
  };

  const filterReservations = (reservations: Reservation[]) => {
    switch (selectedTab) {
      case 0: // All
        return reservations;
      case 1: // Upcoming
        return reservations.filter(r => 
          r.status === ReservationStatus.CONFIRMED || r.status === ReservationStatus.PENDING
        );
      case 2: // Active
        return reservations.filter(r => r.status === ReservationStatus.IN_PROGRESS);
      case 3: // Completed
        return reservations.filter(r => r.status === ReservationStatus.COMPLETED);
      case 4: // Cancelled
        return reservations.filter(
          r => r.status === ReservationStatus.CANCELLED || r.status === ReservationStatus.DECLINED
        );
      default:
        return reservations;
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{t('reservationsPage.loadFailed')}</Alert>
      </Container>
    );
  }

  const filteredReservations = reservations ? filterReservations(reservations) : [];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        {t('reservationsPage.title')}
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        {t('reservationsPage.subtitle')}
      </Typography>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={selectedTab} onChange={(e, newValue) => setSelectedTab(newValue)}>
          <Tab label={t('reservationsPage.allTab', { count: reservations?.length || 0 })} />
          <Tab label={t('reservationsPage.upcoming')} />
          <Tab label={t('status.active')} />
          <Tab label={t('status.completed')} />
          <Tab label={t('status.cancelled')} />
        </Tabs>
      </Box>

      {/* Reservations List */}
      {filteredReservations.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {t('reservationsPage.empty')}
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            {selectedTab === 0 
              ? t('reservationsPage.emptyAll')
              : t('reservationsPage.emptyCategory')}
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate(APP_ROUTES.public.search)}
            sx={{ mt: 2 }}
          >
            {t('nav.searchVehicles')}
          </Button>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {filteredReservations.map((reservation) => (
            <Grid item xs={12} key={reservation.id}>
              <Card>
                <CardContent>
                  <Grid container spacing={3}>
                    {/* Vehicle Info */}
                    <Grid item xs={12} md={3}>
                      {reservation.vehicle?.imageUrls && reservation.vehicle.imageUrls.length > 0 ? (
                        <Box
                          component="img"
                          src={reservation.vehicle.imageUrls[0]}
                          alt={`${reservation.vehicle.make} ${reservation.vehicle.model}`}
                          sx={{
                            width: '100%',
                            height: 150,
                            objectFit: 'cover',
                            borderRadius: 1,
                          }}
                          onError={(e) => {
                            // Fallback if image fails to load
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: '100%',
                            height: 150,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'grey.200',
                            borderRadius: 1,
                          }}
                        >
                          <DirectionsCar sx={{ fontSize: 60, color: 'grey.400' }} />
                        </Box>
                      )}
                    </Grid>

                    {/* Reservation Details */}
                    <Grid item xs={12} md={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="h6">
                          {reservation.vehicle?.make} {reservation.vehicle?.model} ({reservation.vehicle?.year})
                        </Typography>
                        <ReservationStatusChip
                          icon={getStatusIcon(reservation.status)}
                          status={reservation.status}
                          size="small"
                        />
                      </Box>

                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {t('fields.reservationNumber')} {reservation.reservationNumber}
                      </Typography>

                      <Box sx={{ mt: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <CalendarToday fontSize="small" color="action" />
                          <Typography variant="body2">
                            {formatDate(reservation.startDate)} - {reservation.endDate ? formatDate(reservation.endDate) : t('rental.returnDateTbd')}
                          </Typography>
                          <Chip label={t('common.daysCount', { count: reservation.totalDays })} size="small" variant="outlined" />
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <LocationOn fontSize="small" color="action" />
                          <Typography variant="body2">
                            {t('reservationsPage.pickupLabel')} {reservation.pickupLocation}
                          </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <DirectionsCar fontSize="small" color="action" />
                          <Typography variant="body2">
                            {reservation.company?.companyName}
                          </Typography>
                        </Box>
                      </Box>

                      {reservation.notes && (
                        <Box sx={{ mt: 2, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            {t('reservationsPage.specialRequests')}
                          </Typography>
                          <Typography variant="body2">
                            {reservation.notes}
                          </Typography>
                        </Box>
                      )}
                    </Grid>

                    {/* Pricing & Actions */}
                    <Grid item xs={12} md={3}>
                      <Box sx={{ textAlign: 'right' }}>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="text.secondary">{t('fields.totalAmount')}</Typography>
                          <Typography variant="h5" color="primary">
                            {formatCurrency(reservation.totalAmount)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t('common.perDay', { value: formatCurrency(reservation.dailyRate) })}
                          </Typography>
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {reservation.vehicle?.id && (
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() =>
                                navigate(APP_ROUTES.public.vehicleDetails(reservation.vehicle!.id))
                              }
                            >
                              {t('reservationsPage.viewVehicle')}
                            </Button>
                          )}

                          {(reservation.status === ReservationStatus.PENDING || 
                            reservation.status === ReservationStatus.CONFIRMED) && (
                            <Button
                              variant="outlined"
                              color="error"
                              size="small"
                              startIcon={<CancelIcon />}
                              onClick={() => handleCancelClick(reservation)}
                            >{t('common.cancel')}</Button>
                          )}
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Cancel Dialog */}
      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('reservationsPage.cancelReservation')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            {t('reservationsPage.cancelConfirm')}
          </Typography>
          {selectedReservation && (
            <Box sx={{ my: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>{t('reservationsPage.vehicleLabel')}</strong> {selectedReservation.vehicle?.make} {selectedReservation.vehicle?.model}
              </Typography>
              <Typography variant="body2">
                <strong>{t('reservationsPage.datesLabel')}</strong> {formatDate(selectedReservation.startDate)} - {formatDate(selectedReservation.endDate)}
              </Typography>
            </Box>
          )}
          <TextField
            fullWidth
            multiline
            rows={3}
            label={t('dialogs.cancellationReason')}
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)}>{t('reservationsPage.keepReservation')}</Button>
          <Button
            onClick={handleConfirmCancel}
            color="error"
            variant="contained"
            disabled={cancelMutation.isLoading}
          >
            {cancelMutation.isLoading ? t('reservationsPage.cancelling') : t('reservationsPage.confirmCancellation')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};
